import type {
    ModelKey,
    ModelDisplayCategorySummary,
    ModelRawClassVersionSourceSummary,
    ModelProjectConfigRecordSummary,
    ModelRuntimeConfigSummary,
    ModelVersionSummary,
} from '../../../types/modelManagement'
import {
    extractApiErrorDetail,
    getLocalizedApiErrorMessage,
    type TranslateFunction,
} from '../../../utils/apiErrorI18n'
export { RAW_CLASS_LABEL_KEYS } from '../../../utils/rawClassDisplay'

export interface UploadFormValues {
    remark?: string
}

export interface RemarkFormValues {
    remark?: string
}

export interface BindingFormValues {
    boundRawClasses?: string[]
    boundManualTypes?: string[]
}

export type ModelConfigurationStatusKey = 'configured' | 'notConfigured'
export type ModelSelectionStatusKey = 'selected' | 'unselected'
export type ModelScopeTabKey = 'platform' | 'project'

export type DisplayCategoryKey = string

export interface ModelConfigurationRow {
    modelKey: ModelKey
    activeVersion?: ModelVersionSummary
    runtimeConfig?: ModelRuntimeConfigSummary
    statusKey: ModelConfigurationStatusKey
}

export interface DisplayCategoryRow {
    key: DisplayCategoryKey
    category: ModelDisplayCategorySummary
}

export interface ProjectConfigRecordRow {
    projectId: number
    projectCode: string
    projectName: string
    record: ModelProjectConfigRecordSummary
}

export interface RawClassBindingOption {
    value: string
    label: string
    disabled?: boolean
    modelKeys: ModelKey[]
    rawClass: string
    rawClassNorm: string
    displayCategoryKey?: string | null
    isMapped: boolean
    isCurrentModel: boolean
    isHistoricalOnly: boolean
    sourceVersions: ModelRawClassVersionSourceSummary[]
}

export interface ScopeSelectorOption {
    label: string
    value: string
}

export const MODEL_MANAGEMENT_I18N_PREFIX = 'admin:modelManagement'
export const DEFAULT_MAPPING_SET_ID = 'default'
export const PLATFORM_SCOPE_KEY = 'platform'
export const PROJECT_SCOPE_PREFIX = 'project:'
export const MODEL_CONFIGURATION_DETAIL_MODAL_WIDTH =
    'min(1280px, calc(100vw - 48px))'
export const MODEL_CONFIGURATION_TABLE_SCROLL_X = 1010
export const MODEL_VERSION_TABLE_SCROLL_X = 1240
export const BINDING_TABLE_SCROLL_X = 1000
export const VERSION_DETAIL_TABLE_SCROLL_X = 1160
export const MODEL_USAGE_TABLE_SCROLL_X = 740

export const MODEL_KEY_OPTIONS: Array<{ labelKey: string; value: ModelKey }> = [
    { labelKey: 'modelKeys.mosquito', value: 'mosquito' },
    { labelKey: 'modelKeys.insect', value: 'insect' },
]

export const MODEL_CONFIGURATION_STATUS_COLORS: Record<
    ModelConfigurationStatusKey,
    string
> = {
    configured: 'success',
    notConfigured: 'default',
}

export const MODEL_SELECTION_STATUS_COLORS: Record<
    ModelSelectionStatusKey,
    string
> = {
    selected: 'success',
    unselected: 'default',
}

const MODEL_MANAGEMENT_ERROR_KEYS: Record<string, string> = {
    'dataset must contain at least one image':
        'admin:modelValidation.datasets.publishBlockedNoImages',
    'all images must be reviewed before publish':
        'admin:modelValidation.datasets.publishBlockedPendingReviewGeneric',
    'active model version cannot be deleted':
        'admin:modelManagement.messages.deleteVersionInUse',
    'model version is still configured as current version':
        'admin:modelManagement.messages.deleteVersionInUse',
    'display category key already exists':
        'admin:modelManagement.messages.categoryAlreadyExists',
    // The API currently returns this operator-facing Chinese detail for all
    // locales. Keep the stable English detail above for compatibility while
    // translating the live response through the normal locale resources.
    '自定义类型已存在':
        'admin:modelManagement.messages.categoryAlreadyExists',
    '自定義類型已存在':
        'admin:modelManagement.messages.categoryAlreadyExists',
    'model version label already exists for this model':
        'admin:modelManagement.messages.versionLabelAlreadyExists',
    'model version already exists':
        'admin:modelManagement.messages.versionAlreadyExists',
    'model version label exists with different file':
        'admin:modelManagement.messages.versionLabelFileConflict',
    'model artifact storage unavailable':
        'admin:modelManagement.messages.artifactStorageUnavailable',
    'display category not found':
        'admin:modelManagement.messages.categoryNotFound',
    'display category key is required':
        'admin:modelManagement.messages.categoryKeyRequired',
    'display category key must start with a letter and contain only letters, numbers, _ or -':
        'admin:modelManagement.messages.categoryKeyInvalid',
    'display_name_zh_cn is required':
        'admin:modelManagement.messages.categoryNameRequired',
    'display_name_zh_cn must be at most 64 characters':
        'admin:modelManagement.messages.categoryNameTooLong',
    'manual recognition type already exists':
        'admin:modelManagement.messages.manualTypeAlreadyExists',
    'manual recognition type name already exists':
        'admin:modelManagement.messages.manualTypeAlreadyExists',
    'manual recognition type not found':
        'admin:modelManagement.messages.manualTypeNotFound',
    'manual type display name is required':
        'admin:modelManagement.messages.manualTypeNameRequired',
    'manual type display name is too long':
        'admin:modelManagement.messages.manualTypeNameTooLong',
    'model evaluation task has already been superseded':
        'admin:modelValidation.messages.taskAlreadyRerun',
    'model evaluation task is not rerunnable':
        'admin:modelValidation.messages.taskNotRerunnable',
    'model evaluation task samples are no longer available':
        'admin:modelValidation.messages.taskSamplesUnavailable',
}

export const getModelManagementErrorDetail = (
    error: unknown,
    fallback: string,
    t?: TranslateFunction,
): string => {
    if (t) {
        const detail = extractApiErrorDetail(error)
        const translationKey = detail
            ? MODEL_MANAGEMENT_ERROR_KEYS[detail]
            : undefined
        if (translationKey) {
            return t(translationKey)
        }
        return getLocalizedApiErrorMessage(error, fallback, t)
    }
    const detail = extractApiErrorDetail(error)
    if (detail) {
        return detail
    }
    return error instanceof Error ? error.message : fallback
}

export const formatBytes = (value?: number | null): string => {
    if (value === null || value === undefined) {
        return '-'
    }
    if (value < 1024) {
        return `${value} B`
    }
    if (value < 1024 * 1024) {
        return `${(value / 1024).toFixed(1)} KB`
    }
    return `${(value / (1024 * 1024)).toFixed(1)} MB`
}

export const hasSuccessfulValidation = (
    version?: ModelVersionSummary,
): boolean => version?.validation_result?.success === true

export const countClassNames = (version: ModelVersionSummary): number =>
    version.class_names ? Object.keys(version.class_names).length : 0

export const normalizeRawClass = (value: string): string =>
    value.trim().toLowerCase()

export const encodeBindingValue = (rawClassNorm: string): string => rawClassNorm

export const encodeProjectScopeValue = (projectId: number): string =>
    `${PROJECT_SCOPE_PREFIX}${projectId}`

export const parseScopeValue = (
    value: string,
):
    | { scopeType: 'platform'; projectId: null }
    | { scopeType: 'project'; projectId: number } => {
    if (value.startsWith(PROJECT_SCOPE_PREFIX)) {
        const projectId = Number(value.slice(PROJECT_SCOPE_PREFIX.length))
        return {
            scopeType: 'project',
            projectId: Number.isFinite(projectId) ? projectId : 0,
        }
    }
    return { scopeType: 'platform', projectId: null }
}

export const getModelStatusKey = (
    activeVersion?: ModelVersionSummary,
): ModelConfigurationStatusKey => {
    if (activeVersion && hasSuccessfulValidation(activeVersion)) {
        return 'configured'
    }
    return 'notConfigured'
}
